import React, { useEffect } from "react";
import { useDeviceSelectors, useMobileOrientation } from "react-device-detect";
import { useLocation } from "react-router-dom";
import { isPresent } from "@apl-digital/utils";

import ConfirmationModal from "@/base/components/Modals/ConfirmationModal";
import DeliveryMethodModal from "@/base/components/Modals/DeliveryMethod/DeliveryMethodModal";
import SearchModalWrapper from "@/base/components/Modals/MenuSearch/SearchModalWrapper";
import useEffectAsync from "@/base/hooks/useEffectAsync";
import { NavigationPath } from "@/constants/navigation";
import { useSalesPointContext } from "@/context/SalesPointProvider";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  hideDeliveryMethodModal,
  hideProductSearchModal,
  setIsLandscape,
  setIsTouchDevice,
  showDeliveryMethodModal,
} from "@/store/layout";
import {
  selectAvailableDiningOptions,
  selectIsKitchenOpen,
} from "@/store/salesPoint/selectors";
import {
  initializeDiningCartWithMigration,
  selectNewShoppingCart,
  selectShoppingCart,
} from "@/store/shoppingCart";
import { getTheatreResource } from "@/store/theatreResource";
import { selectUserId } from "@/store/user";
import { isStatus } from "@/store/utils";

import { type ConfirmationModalContent, ModalContext } from "./ModalContext";

type ModalContainerProps = {
  children: React.ReactNode;
};

/**
 * @deprecated
 * This should get deprecated, since every modal should be rendered where its needed
 */
export const ModalContainer: React.FC<ModalContainerProps> = ({ children }) => {
  const [confirmationModal, setConfirmationModal] =
    React.useState<ConfirmationModalContent | null>(null);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isLandscape } = useMobileOrientation();
  const [selectors] = useDeviceSelectors(globalThis.navigator.userAgent) as [
    { isMobile: boolean },
  ];

  const [{ configuration }] = useSalesPointContext();

  const shoppingCart = useAppSelector(selectShoppingCart);
  const newShoppingCart = useAppSelector(selectNewShoppingCart);
  const userId = useAppSelector(selectUserId);
  const availableDiningOptions = useAppSelector(selectAvailableDiningOptions);

  const isProductSearchModalVisible = useAppSelector(
    (state) => state.layout.isProductSearchModalVisible,
  );
  const isDeliveryMethodModalVisible = useAppSelector(
    (state) => state.layout.isDeliveryMethodModalVisible,
  );

  const isKitchenOpen = useAppSelector(selectIsKitchenOpen);

  useEffectAsync(async () => {
    if (
      !isPresent(configuration) ||
      !isStatus(shoppingCart, ["idle", "failed"]) ||
      !isStatus(newShoppingCart, ["idle", "failed"]) ||
      location.pathname !== NavigationPath.Home ||
      !isKitchenOpen
    ) {
      return;
    }

    if (
      availableDiningOptions.length === 1 &&
      !availableDiningOptions[0].IsPreorderEnabled
    ) {
      try {
        let theatreResourceId = 0;

        if (isPresent(configuration.DefaultTableBarcode)) {
          const theatreResource = await dispatch(
            getTheatreResource({
              barcodeWithoutChecksum: configuration.DefaultTableBarcode,
            }),
          ).unwrap();

          theatreResourceId = theatreResource.ID;
        }

        await dispatch(
          initializeDiningCartWithMigration({
            theatreResourceId: theatreResourceId,
            dttmRequestedDelivery: null,
            diningOptionId: availableDiningOptions[0].ID,
            customerPersonId: userId,
          }),
        ).unwrap();
      } catch {
        // Leave the cart uninitialized if automatic creation fails.
      }
    } else {
      dispatch(showDeliveryMethodModal());
    }
  }, [shoppingCart, newShoppingCart, configuration, location]);

  useEffect(() => {
    if (selectors.isMobile) {
      dispatch(setIsTouchDevice(true));
      dispatch(setIsLandscape(isLandscape));
    } else {
      dispatch(setIsTouchDevice(false));
      dispatch(setIsLandscape(false));
    }
  }, [isLandscape, selectors.isMobile]);

  return (
    <ModalContext.Provider
      value={{
        showConfirmationModal: (modal) => setConfirmationModal(modal),
        hideConfirmationModal: () => setConfirmationModal(null),
      }}
    >
      <ConfirmationModal
        isOpen={isPresent(confirmationModal)}
        onClose={() => setConfirmationModal(null)}
        modalObject={confirmationModal}
      />
      <SearchModalWrapper
        isOpen={isProductSearchModalVisible}
        onClose={() => dispatch(hideProductSearchModal())}
      />
      <DeliveryMethodModal
        isOpen={isDeliveryMethodModalVisible}
        onClose={() => dispatch(hideDeliveryMethodModal())}
      />
      {children}
    </ModalContext.Provider>
  );
};
